import React from 'react';
import {
  Box, Container, Typography, Divider, Chip, Link, List, ListItem, ListItemText, Paper
} from '@mui/material';

const TERMS_VERSION = '2025-09-29';
const LAST_UPDATED = 'September 29, 2025';

export default function Terms() {
  return (
    <Box sx={{ py: { xs: 4, md: 6 } }}>
      <Container maxWidth="md">
        <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 2 }}>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
                Terms & Conditions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last updated {LAST_UPDATED} • Version {TERMS_VERSION}
              </Typography>
            </Box>
            <Chip label="Ski Fleet" />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Table of contents */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="overline" color="text.secondary">On this page</Typography>
            <List dense>
              {[
                ['1. Acceptance of Terms', 'acceptance'],
                ['2. Description of Service', 'description'],
                ['3. User Accounts', 'accounts'],
                ['4. Data Collection & Use', 'data'],
                ['5. AI & Analytics', 'ai'],
                ['6. Intellectual Property', 'ip'],
                ['7. Plans & Billing', 'billing'],
                ['8. User Responsibilities', 'responsibilities'],
                ['9. Termination', 'termination'],
                ['10. Limitation of Liability', 'liability'],
                ['11. Governing Law', 'law'],
                ['12. Changes', 'changes'],
              ].map(([label, id]) => (
                <ListItem key={id} sx={{ py: 0 }}>
                  <ListItemText
                    primary={<Link href={`#${id}`} underline="hover">{label}</Link>}
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Content */}
          <Section id="acceptance" title="1. Acceptance of Terms">
            <P>
              By creating an account or using the Ski Fleet platform (“Service”), you agree to these Terms and
              Conditions (“Terms”). If you do not agree, do not use the Service.
            </P>
          </Section>

          <Section id="description" title="2. Description of Service">
            <P>
              Ski Fleet is a platform that enables athletes, coaches, retailers, and manufacturers to manage ski
              fleets, record ski and wax tests, track race performance, and access related data and insights.
            </P>
          </Section>

          <Section id="accounts" title="3. User Accounts">
            <UL items={[
              'You must provide accurate information when registering.',
              'You are responsible for safeguarding your login credentials.',
              'You may not impersonate another person or misrepresent your affiliation with any entity.'
            ]} />
          </Section>

          <Section id="data" title="4. Data Collection & Use">
            <Subheading>4.1 Personal Data</Subheading>
            <P>
              We collect personal information (such as name, email, and login details) to operate your account. This is
              handled in accordance with our <Link href="/privacy">Privacy Policy</Link>.
            </P>

            <Subheading>4.2 Performance Data</Subheading>
            <P>
            By using the Service, you agree that ski performance data, wax test data, race logs, and related inputs 
            you submit (“Performance Data”) may be:
            </P>
            <UL items={[
              'Stored and processed by Ski Fleet,',
              'Used to improve the Service and develop analytics, including AI models,',
              'Aggregated and anonymized for research, analytics, and commercial purposes.'
            ]} />

            <Subheading>4.3 Data Sharing</Subheading>
            <P>
                You acknowledge and agree that Ski Fleet may:
            </P>
            <UL items={[
              'Share aggregated and anonymized Performance Data with third parties such as ski manufacturers, wax companies, and research institutions.',
              'License or sell aggregated and anonymized datasets for commercial purposes. We will not sell or share personally identifiable information without your consent.',
            ]} />
          </Section>

          <Section id="ai" title="5. AI & Analytics">
            <P>
                Performance Data may be used to train machine learning and AI models. 
                These models may provide ski/wax recommendations, performance predictions, 
                and other insights. You grant Ski Fleet a perpetual, irrevocable, royalty-free 
                license to use your submitted Performance Data for these purposes, provided 
                that such use does not disclose your personal identity without consent.
            </P>
          </Section>

          <Section id="ip" title="6. Intellectual Property">
            <UL items={[
              'All software, branding, and platform content remain the property of Ski Fleet.',
              'You retain ownership of your Performance Data but grant Ski Fleet the licenses described in Section 4.'
            ]} />
          </Section>

          <Section id="billing" title="7. Plans & Billing">
            <UL items={[
              'Access to certain features may require a subscription.',
              'Fees, billing cycles, and cancellation policies are detailed at the time of purchase.'
            ]} />
          </Section>

          <Section id="responsibilities" title="8. User Responsibilities">
            <UL items={[
              'You may not upload false, misleading, or illegal content.',
              'You may not attempt to hack, copy, or reverse-engineer the platform.'
            ]} />
          </Section>

          <Section id="termination" title="9. Termination">
            <P>We reserve the right to suspend or terminate accounts that violate these Terms.</P>
          </Section>

          <Section id="liability" title="10. Limitation of Liability">
            <P>
              The Service is provided “as is” without warranties of any kind. Ski Fleet is not responsible for damages
              resulting from reliance on recommendations, data, or Service availability.
            </P>
          </Section>

          <Section id="law" title="11. Governing Law">
            <P>These Terms are governed by the laws of Calgary, Alberta, Canada.</P>
          </Section>

          <Section id="changes" title="12. Changes">
            <P>We may update these Terms. Continued use of the Service after changes constitutes acceptance.</P>
          </Section>
        </Paper>
      </Container>
    </Box>
  );
}

/* Helpers */
function Section({ id, title, children }) {
  return (
    <Box id={id} sx={{ scrollMarginTop: 96, mt: 4 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{title}</Typography>
      {children}
    </Box>
  );
}
function Subheading({ children }) {
  return <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2 }}>{children}</Typography>;
}
function P({ children }) {
  return <Typography variant="body1" sx={{ mb: 1.5 }}>{children}</Typography>;
}
function UL({ items }) {
  return (
    <Box component="ul" sx={{ pl: 3, my: 1 }}>
      {items.map((t, i) => (
        <Typography key={i} component="li" variant="body1" sx={{ mb: 0.75 }}>
          {t}
        </Typography>
      ))}
    </Box>
  );
}
